import React, { useState, useEffect } from 'react'
import { Modal } from '@peracto/peracto-ui'
import { GET_LIST, GET_ONE, useClient } from '@peracto/client'
import { useConfig } from '@peracto/peracto-config'
import { StyledCheckbox, LoadingIcon } from '@peracto/peracto-ui'
import axios from 'axios'

import * as S from '../styled'
import { ProductVariantOptions } from './ProductVariantOptions'

import { getToken } from '../basket-utils'

export const convertQueryToArray = path => {
    const splitPath = path.split('?')
    if (splitPath.length === 1) {
        return []
    }
    const splitQuery = splitPath[splitPath.length - 1].split('&')
    const splitQueryItems = splitQuery.map(i => i.split('='))

    return splitQueryItems.map(item => {
        return { code: item[0], value: item[1] }
    })
}

export const convertArrayToQueryString = queryArr => {
    return queryArr
        .filter(item => item.code !== 'slug')
        .map(item => `${encodeURIComponent(item.code)}=${encodeURIComponent(item.value)}`)
        .join('&')
}

const priceOrder = ['clearancePrice', 'salePrice', 'price', 'rrp']

const OrdersCreateModal = ({
    searchProduct,
    setSearchProduct,
    searchSku,
    setSearchSku,
    showProductModal,
    setShowProductModal,
    onAddProducts,
}) => {
    const { client } = useClient()
    const config = useConfig()

    const { orders } = config.get('features', {})
    const API_URL = config.get('api')
    const [products, setProducts] = useState()
    const [variantOptions, setVariantOptions] = useState([])
    const [selectedVariantOptions, setSelectedVariantOptions] = useState([])

    const [selectedProduct, setSelectedProduct] = useState()
    const [productPrice, setProductPrice] = useState('')
    const [readyToAdd, setReadyToAdd] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const token = getToken()

    // show prices as tax feature flag - default: true
    const includeTax =
        orders && orders.hasOwnProperty('includeTax') ? orders.includeTax === true : true

    const fetchProducts = async () => {
        if (!!searchProduct || !!searchSku) {
            setIsLoading(true)
            setSelectedProduct([])
            setSelectedVariantOptions([])
            setVariantOptions([])
            setReadyToAdd(false)
            setProductPrice(null)

            const { data } = await client(GET_LIST, 'products', {
                id: 'products',
                filter: {
                    productName: searchProduct,
                    sku: searchSku,
                    purchasable: true,
                    status: 'active',
                },
            })

            setProducts(data)
            setIsLoading(false)
        }
    }

    const setPrice = prices => {
        let p = null

        priceOrder.forEach(priceType => {
            const priceValue = prices?.[priceType]?.[includeTax ? 'incTax' : 'excTax']
            if (!p && priceValue) {
                p = (priceValue / 100).toFixed(2)
            }
        })

        setProductPrice(p)
    }

    const getStorefrontProductData = async (slug, query) => {
        const { data: product } = await axios.get(
            `${API_URL}/storefront/product/${slug}${query ? `?${query}` : ''}`
        )
        return product
    }

    const getProductDetailData = async id => {
        const { data: product } = await axios.get(`${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        return product
    }

    const getVariantOptions = async (slug, query) => {
        const product = await getStorefrontProductData(slug, query)

        const productVariant = product?.variant || product?.displayVariant

        if (product?.options?.length > 0 && productVariant) {
            setSelectedProduct({
                id: `/product-variants/${productVariant}`,
                variantId: `/product-variants/${productVariant}`,
                sku: product.parentSku,
                slug: slug,
                quantity: 1,
            })
            setPrice(product?.prices)
            setReadyToAdd(true)
        }

        setVariantOptions(product?.options)

        return product
    }

    const onClickProduct = async product => {
        setReadyToAdd(false)
        setSelectedVariantOptions([])
        setVariantOptions([])

        if (selectedProduct?.id === product['@id']) {
            setProductPrice(null)
            setSelectedProduct(null)
        } else {
            const productDetails = await getProductDetailData(product['@id'])

            setSelectedProduct({
                id: product['@id'],
                sku: product.sku,
                slug: productDetails.slug,
            })

            const { options, prices, variant, displayVariant } = await getStorefrontProductData(
                productDetails.slug
            )

            setPrice(prices)

            if (options?.length === 0) {
                setVariantOptions([])
                setReadyToAdd(true)
            } else {
                setVariantOptions(options)

                if (variant || displayVariant) {
                    setReadyToAdd(true)
                }
            }
        }
    }

    useEffect(() => {
        if (showProductModal) {
            fetchProducts()
        } else {
            setSelectedProduct(null)
        }
    }, [showProductModal])

    return (
        <Modal
            className="modal-lg"
            isVisible={showProductModal}
            title="Add Product"
            close={() => setShowProductModal(false)}
            buttons={[
                {
                    type: 'btn-outline-secondary',
                    text: 'Close',
                    action: () => {
                        setShowProductModal(false)
                    },
                },
                {
                    type: 'btn-success',
                    text: 'Add Product',
                    disabled: !readyToAdd,
                    action: async () => {
                        const endpoint = selectedProduct?.id?.split('/')

                        if (endpoint) {
                            const { data: product } = await client(GET_ONE, endpoint[0], {
                                id: selectedProduct.id,
                            })

                            const quantity = product.minimumOrderQuantity || 1

                            const cloneProduct = { ...selectedProduct }

                            if (cloneProduct?.variantId) {
                                delete cloneProduct.id
                            }

                            onAddProducts({
                                ...cloneProduct,
                                quantity,
                            })
                        }
                    },
                },
            ]}
        >
            <S.ModalContent>
                <div className="!pb-4 row search-row">
                    <div className="col-12 col-lg-5">
                        <input
                            type="text"
                            className="form-control"
                            onChange={e => {
                                setSearchProduct(e.target.value)
                            }}
                            onKeyDown={e => {
                                if (e.keyCode === 13 || e.which === 13) {
                                    fetchProducts()
                                }
                            }}
                            value={searchProduct}
                            placeholder="Product Name"
                        />
                    </div>
                    <div className="col-12 col-lg-5">
                        <input
                            type="text"
                            className="form-control"
                            onChange={e => {
                                setSearchSku(e.target.value)
                            }}
                            onKeyDown={e => {
                                if (e.keyCode === 13 || e.which === 13) {
                                    fetchProducts()
                                }
                            }}
                            value={searchSku}
                            placeholder="Product SKU"
                        />
                    </div>

                    <div className="col-12 col-lg-2">
                        <div className="!flex !justify-end">
                            <button className="btn btn-success" onClick={() => fetchProducts()}>
                                Search
                            </button>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="!h-full !w-full">
                        <div className="!flex !grow !items-center !flex-col !justify-center !h-full">
                            <LoadingIcon />
                        </div>
                    </div>
                ) : (
                    <>
                        {products?.length > 0 ? (
                            <div className="!w-full">
                                <table className="table !text-left">
                                    <colgroup>
                                        <col width="10%" />
                                        <col width="15%" />
                                        <col width="75%" />
                                    </colgroup>

                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th></th>
                                            <th className="!whitespace-nowrap">Product Name</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {products?.map((product, idx) => {
                                            return (
                                                <tr
                                                    key={product['@id']}
                                                    className={
                                                        selectedProduct?.sku === product.sku
                                                            ? 'selected'
                                                            : ''
                                                    }
                                                >
                                                    <td>
                                                        <StyledCheckbox
                                                            checked={
                                                                selectedProduct?.sku === product.sku
                                                            }
                                                            onChange={() => onClickProduct(product)}
                                                        />
                                                    </td>
                                                    <td onClick={() => onClickProduct(product)}>
                                                        {product?.primaryImage?.location && (
                                                            <img
                                                                src={
                                                                    product?.primaryImage?.location
                                                                }
                                                                title={product.name}
                                                                alt={product.name}
                                                                style={{ width: '40px' }}
                                                            />
                                                        )}
                                                    </td>
                                                    <td onClick={() => onClickProduct(product)}>
                                                        <p className="!mb-0">
                                                            {product?.name || '-'}
                                                        </p>

                                                        <p className="!mb-0 text-grey text-small">
                                                            {product?.sku}
                                                        </p>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="!mt-6 !mb-0 !text-center">No products found.</p>
                        )}

                        {variantOptions?.length > 0 &&
                            variantOptions.map((option, idx) => (
                                <ProductVariantOptions
                                    key={`product_variant_${idx}`}
                                    containerProps={{
                                        mb: '15px',
                                    }}
                                    code={option.key}
                                    onSelectOption={({ code, value }) => {
                                        const filteredQueryArr = selectedVariantOptions.filter(
                                            q => q.code !== code
                                        )

                                        const newArr = [...filteredQueryArr, { code, value }]
                                        setSelectedVariantOptions(newArr)

                                        const q = convertArrayToQueryString(newArr)
                                        getVariantOptions(selectedProduct.slug, q)
                                    }}
                                    {...option}
                                />
                            ))}

                        {productPrice && (
                            <h6
                                className="!pt-2 !pb-0 !mb-0 !text-right !font-bold !border-t !border-t-[#dee2e6]"
                                style={{
                                    position: 'sticky',
                                    bottom: '0',
                                    backgroundColor: '#fff',
                                }}
                            >
                                Price: £{productPrice}
                            </h6>
                        )}
                    </>
                )}
            </S.ModalContent>
        </Modal>
    )
}

export default OrdersCreateModal
